// 0130 V4 · SOLICITUDES DE ANULACIÓN (segregación): la responsable o una operadora del turno pide anular
// un cobro desde «Mi turno»; tesorería lo EJECUTA aquí (caja.recibo.anular, ligada a la solicitud) o lo
// RECHAZA. Quien la pidió no la ejecuta. Si no hay solicitudes pendientes, no se muestra nada.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { claves, useSolicitudesAnulacion } from '../api/consultas';
import type { SolicitudAnulacion } from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fechaHora, pesos } from '../comun/formato';
import { NOMBRE_CAUSA } from './textos';

export function SolicitudesAnulacion({
  empresaId,
  alCambiar,
}: {
  empresaId: string;
  /** Avisa que se anuló o rechazó algo (para refrescar la grilla de recibos). */
  alCambiar?: () => void;
}) {
  const q = useSolicitudesAnulacion(empresaId, 'PENDIENTE');
  const [accion, setAccion] = useState<null | {
    tipo: 'ejecutar' | 'rechazar';
    s: SolicitudAnulacion;
  }>(null);
  if (!q.data?.length) return null;
  return (
    <section className="recuadro" aria-label="Solicitudes de anulación">
      <h3>Solicitudes de anulación pendientes</h3>
      <p className="apagado">
        Las pide la responsable o una operadora del turno; las ejecuta o rechaza tesorería. Quien la
        pidió no la ejecuta.
      </p>
      <table className="grilla compacta" aria-label="Solicitudes de anulación pendientes">
        <thead>
          <tr>
            <th>Pedida</th>
            <th>Recibo</th>
            <th>Caja · turno</th>
            <th>Pidió</th>
            <th>Causa</th>
            <th className="al-der">Total</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {q.data.map((s) => (
            <tr key={s.solicitudId} data-solicitud={s.solicitudId}>
              <td>{fechaHora(s.solicitadoEn)}</td>
              <td className="mono">{s.recibo}</td>
              <td>
                {s.caja} · {s.turno}
                {s.turnoCerrado ? ' (cerrado)' : ''}
              </td>
              <td>{s.solicitadoPor.nombre}</td>
              <td>
                {NOMBRE_CAUSA[s.causa]}
                {s.notaSolicitud ? ` · «${s.notaSolicitud}»` : ''}
              </td>
              <td className="al-der">{pesos(s.total)}</td>
              <td className="al-der">
                <button type="button" onClick={() => setAccion({ tipo: 'rechazar', s })}>
                  Rechazar
                </button>{' '}
                <button
                  type="button"
                  className="primario"
                  onClick={() => setAccion({ tipo: 'ejecutar', s })}
                >
                  Anular
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {accion && (
        <AccionSolicitud
          empresaId={empresaId}
          tipo={accion.tipo}
          solicitud={accion.s}
          alCerrar={(hecho) => {
            setAccion(null);
            if (hecho) alCambiar?.();
          }}
        />
      )}
    </section>
  );
}

function AccionSolicitud({
  empresaId,
  tipo,
  solicitud: s,
  alCerrar,
}: {
  empresaId: string;
  tipo: 'ejecutar' | 'rechazar';
  solicitud: SolicitudAnulacion;
  alCerrar: (hecho: boolean) => void;
}) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  const [texto, setTexto] = useState(
    tipo === 'ejecutar' ? `Solicitud de ${s.solicitadoPor.nombre}: ${NOMBRE_CAUSA[s.causa]}` : '',
  );
  const enviar = useMutation({
    mutationFn: () => {
      if (tipo === 'rechazar')
        return api.post(`/caja/solicitudes-anulacion/${s.solicitudId}/rechazar`, {
          ...(texto.trim() ? { nota: texto.trim() } : {}),
        });
      const cuerpo = { motivo: texto.trim(), version: s.reciboVersion, solicitudId: s.solicitudId };
      return api.post(`/caja/recibos/${s.reciboId}/anular`, cuerpo, {
        idempotencia: intentos.clave('ejecutar-anulacion', { id: s.reciboId, ...cuerpo }),
      });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: claves.caja(empresaId) });
      alCerrar(true);
    },
    onError: (e) => intentos.cerrar(e),
  });
  const titulo =
    tipo === 'ejecutar' ? `Anular ${s.recibo}` : `Rechazar la solicitud de ${s.recibo}`;
  const falta = tipo === 'ejecutar' && texto.trim().length < 3;
  return (
    <Modal
      titulo={titulo}
      ancho="angosto"
      alCerrar={() => !enviar.isPending && alCerrar(false)}
      pie={
        <>
          <button type="button" onClick={() => alCerrar(false)} disabled={enviar.isPending}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            disabled={enviar.isPending || falta}
            onClick={() => enviar.mutate()}
          >
            {enviar.isPending ? 'Guardando…' : tipo === 'ejecutar' ? 'Anular' : 'Rechazar'}
          </button>
        </>
      }
    >
      <p>
        {s.caja} · {s.turno} · {pesos(s.total)} · pidió {s.solicitadoPor.nombre}
      </p>
      <label className="campo">
        <span className="etiqueta">{tipo === 'ejecutar' ? 'Motivo' : 'Nota (opcional)'}</span>
        <input
          aria-label={tipo === 'ejecutar' ? 'Motivo de la anulación' : 'Nota del rechazo'}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
        />
      </label>
      {enviar.isError && <PanelError error={enviar.error} />}
    </Modal>
  );
}
